import React from "react";
import { toast } from "sonner";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { UserRole } from "@modules/authentication/roles";
import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { UserService } from "@modules/user/user.service";
import { RoleBadge, UsersTable } from "@modules/user/components/UsersTable";
import { UserRoleSelect } from "@modules/user/components/UserRoleSelect";
import { ALL_ROLES, useUsersTable } from "@modules/user/useUsersTable";
import {
  getUserDisplayName,
  toRole,
  type UserViewModel,
} from "@modules/user/userViewModel";
import { capitalisedSentence } from "@util/string.util";
import { Routes } from "~/routes";

const KNOWN_ROLES = new Set<string>(Object.values(UserRole));

export const SELF_ROLE_CHANGE_REASON = "You can't change your own role";

const UserManagementPage = () => {
  const { currentUser } = useCurrentUser();
  const table = useUsersTable();
  const { updateUser, reload, selectedRole } = table;
  const userService = useInjection<UserService>(DependencyType.UserService);

  const [assignableRoles, setAssignableRoles] = React.useState<UserRole[]>([]);
  const [pendingUserIds, setPendingUserIds] = React.useState<Set<string>>(
    new Set()
  );

  React.useEffect(() => {
    userService
      .getAssignableRoles()
      .then((roles) =>
        setAssignableRoles(
          roles.filter((role): role is UserRole => KNOWN_ROLES.has(role))
        )
      )
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load assignable roles");
      });
  }, [userService]);

  const setPending = React.useCallback((userId: string, pending: boolean) => {
    setPendingUserIds((previous) => {
      const next = new Set(previous);
      if (pending) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  }, []);

  const handleRoleChange = React.useCallback(
    async (user: UserViewModel, role: UserRole) => {
      const previousRole = user.role;
      setPending(user.id, true);
      updateUser(user.id, { role });

      try {
        const roles = await userService.setUserRole(user.id, role);
        updateUser(user.id, { role: toRole(roles) });
        toast.success(
          `${getUserDisplayName(user)} is now ${capitalisedSentence(role)}`
        );

        if (selectedRole !== ALL_ROLES) {
          reload();
        }
      } catch (error) {
        updateUser(user.id, { role: previousRole });
        toast.error(
          `Failed to change role: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      } finally {
        setPending(user.id, false);
      }
    },
    [reload, selectedRole, setPending, updateUser, userService]
  );

  const renderRole = React.useCallback(
    (user: UserViewModel) => {
      if (assignableRoles.length === 0) {
        return <RoleBadge role={user.role} />;
      }

      return (
        <UserRoleSelect
          role={user.role}
          roles={assignableRoles}
          userLabel={getUserDisplayName(user)}
          isPending={pendingUserIds.has(user.id)}
          disabledReason={
            user.id === currentUser?.id ? SELF_ROLE_CHANGE_REASON : undefined
          }
          onSelect={(role) => void handleRoleChange(user, role)}
        />
      );
    },
    [assignableRoles, currentUser?.id, handleRoleChange, pendingUserIds]
  );

  return (
    <PageTemplate
      title="User Management"
      breadcrumbs={[{ label: "User Management", path: Routes.UserManagement }]}
      subtitle="Promote and demote users of the PES ScentsCheck platform. Changes sign the user out so their new access applies immediately."
      activeItem="User Management"
      headerRule
    >
      <UsersTable table={table} renderRole={renderRole} />
    </PageTemplate>
  );
};

export default UserManagementPage;
